import { useCallback, useState } from 'react';
import { DashboardWidgetSidebarItem } from '../components';
import { VariableDto, WidgetDto } from '@thingslog/repositories';

interface UseDashboardWidgetSidebarActionsResult {
  dashboardWidgetSideWidgets: DashboardWidgetSidebarItem[];
  handleCreateVariablesWidget: () => void;
  handleSetVariablesWidget: () => void;
  handleCreateAddVariableWidget: () => void;
  handleCreateEditVariableWidget: (variable: VariableDto) => void;
  handleCreateAddDashboardWidget: () => void;
  handleCreateEditDashboardWidget: () => void;
  handleCreateEditDashboardTimeframeWidget: () => void;
  handleCreateDashboardWidgetsWidget: () => void;
  handleSetDashboardWidgetsWidget: () => void;
  handleCreateAddDashboardWidgetWidget: () => void;
  handleCreateEditDashboardWidgetWidget: (dashboardWidget: WidgetDto) => void;
  handleCreateEditDashboardWidgetTimeframeWidget: (dashboardWidget: WidgetDto) => void;
  handleCloseSidebarWidget: (widgetId: string) => void;
  handleCloseAllSidebarWidgets: () => void;
}

const getSidebarWidgetKey = (widget: DashboardWidgetSidebarItem): string => {
  if (widget.type === 'variable-edit') {
    return `${widget.type}:${widget.variableId}`;
  }

  if (
    widget.type === 'dashboard-widget-edit' ||
    widget.type === 'dashboard-widget-timeframe-edit'
  ) {
    return `${widget.type}:${widget.dashboardWidgetId}`;
  }

  return widget.type;
};

export const useDashboardWidgetSidebarActions = (): UseDashboardWidgetSidebarActionsResult => {
  const [sidebarWidgets, setSidebarWidgets] = useState<DashboardWidgetSidebarItem[]>([]);

  const prependSidebarWidget = useCallback((widget: DashboardWidgetSidebarItem): void => {
    setSidebarWidgets((prev: DashboardWidgetSidebarItem[]): DashboardWidgetSidebarItem[] => {
      const widgetKey = getSidebarWidgetKey(widget);
      const existingWidget = prev.find(
        (sidebarWidget: DashboardWidgetSidebarItem): boolean =>
          getSidebarWidgetKey(sidebarWidget) === widgetKey
      );

      if (!existingWidget) {
        return [widget, ...prev];
      }

      return [
        existingWidget,
        ...prev.filter(
          (sidebarWidget: DashboardWidgetSidebarItem): boolean =>
            sidebarWidget.id !== existingWidget.id
        ),
      ];
    });
  }, []);

  const handleCreateAddVariableWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'variable-add',
    });
  }, [prependSidebarWidget]);

  const handleCreateEditVariableWidget = useCallback(
    (variable: VariableDto): void => {
      prependSidebarWidget({
        id: crypto.randomUUID(),
        type: 'variable-edit',
        variableId: variable.id,
      });
    },
    [prependSidebarWidget]
  );

  const handleCreateVariablesWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'variables',
    });
  }, [prependSidebarWidget]);

  const handleSetVariablesWidget = useCallback((): void => {
    setSidebarWidgets([
      {
        id: crypto.randomUUID(),
        type: 'variables',
      },
    ]);
  }, []);

  const handleCreateAddDashboardWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'dashboard-add',
    });
  }, [prependSidebarWidget]);

  const handleCreateEditDashboardWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'dashboard-edit',
    });
  }, [prependSidebarWidget]);

  const handleCreateEditDashboardTimeframeWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'dashboard-timeframe-edit',
    });
  }, [prependSidebarWidget]);

  const handleCreateDashboardWidgetsWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'dashboard-widgets',
    });
  }, [prependSidebarWidget]);

  const handleSetDashboardWidgetsWidget = useCallback((): void => {
    setSidebarWidgets([
      {
        id: crypto.randomUUID(),
        type: 'dashboard-widgets',
      },
    ]);
  }, []);

  const handleCreateAddDashboardWidgetWidget = useCallback((): void => {
    prependSidebarWidget({
      id: crypto.randomUUID(),
      type: 'dashboard-widget-add',
    });
  }, [prependSidebarWidget]);

  const handleCreateEditDashboardWidgetWidget = useCallback(
    (dashboardWidget: WidgetDto): void => {
      prependSidebarWidget({
        id: crypto.randomUUID(),
        type: 'dashboard-widget-edit',
        dashboardWidgetId: dashboardWidget.id,
      });
    },
    [prependSidebarWidget]
  );

  const handleCreateEditDashboardWidgetTimeframeWidget = useCallback(
    (dashboardWidget: WidgetDto): void => {
      prependSidebarWidget({
        id: crypto.randomUUID(),
        type: 'dashboard-widget-timeframe-edit',
        dashboardWidgetId: dashboardWidget.id,
      });
    },
    [prependSidebarWidget]
  );

  const handleCloseSidebarWidget = (widgetId: string): void => {
    setSidebarWidgets((prev: DashboardWidgetSidebarItem[]) => {
      return prev.filter((widget: DashboardWidgetSidebarItem) => widget.id !== widgetId);
    });
  };

  const handleCloseAllSidebarWidgets = (): void => {
    setSidebarWidgets([]);
  };

  return {
    dashboardWidgetSideWidgets: sidebarWidgets,
    handleCreateVariablesWidget,
    handleSetVariablesWidget,
    handleCreateAddVariableWidget,
    handleCreateEditVariableWidget,
    handleCreateAddDashboardWidget,
    handleCreateEditDashboardWidget,
    handleCreateEditDashboardTimeframeWidget,
    handleCreateDashboardWidgetsWidget,
    handleSetDashboardWidgetsWidget,
    handleCreateAddDashboardWidgetWidget,
    handleCreateEditDashboardWidgetWidget,
    handleCreateEditDashboardWidgetTimeframeWidget,
    handleCloseSidebarWidget,
    handleCloseAllSidebarWidgets,
  };
};
